// Terrain-Kacheln um den Spieler. Konzept §12.1: ein Korridor, keine Welt —
// 9×9 × 64 m decken ~576 m ab, der Nebel frisst den Rest.
//
// Designziele:
//   1. Kein Ruckler an Kachelgrenzen (Recycling, Bau über Frames).
//   2. Draw Calls < 90 (eine Mesh pro Kachel, max 81).
//   3. Vertexfarben statt Texturen (Stil §12.4, billig).

import * as THREE from 'three';
import {
  TERRAIN_TILE_SIZE, TERRAIN_VIEW_TILES, WORLD_SEED,
} from '../../shared/constants.js';
import { terrainHeight, terrainHeightAt } from '../../shared/terrain.js';
import { projectToTrack, biomeAt, STATIONS, trackPoint } from '../../shared/track.js';
import { rand01 } from '../../shared/rng.js';
import { mat, varyColor } from '../render/style.js';

const TILE = TERRAIN_TILE_SIZE;       // 64 m
const VIEW = TERRAIN_VIEW_TILES;      // 9
const HALF = (VIEW - 1) >> 1;        // 4
const BUILDS_PER_FRAME = 2;
const SKIRT_DEPTH = 3.5;

// LOD nach Chebyshev-Abstand zur Spielerkachel.
//   near (≤1): ~2 m Vertexabstand  → 32 Segmente
//   far  (>1): ~8 m                 →  8 Segmente
//
// LOD-Risse: feine und grobe Nachbarn teilen keine Kantenvertices. Edge-
// Snapping bräuchte pro Kachel Kenntnis aller Nachbar-LODs und teure
// Nachberechnung beim LOD-Wechsel. Stattdessen: Schürzenrand nach unten
// (SKIRT_DEPTH). Der Spalt wird von oben und schräg verdeckt — beim Blick
// vom Zug aus völlig ausreichend, Kosten ≈ null, und Kacheln bleiben
// unabhängig baubar (wichtig für den Frame-Budget-Bau).
const LOD_NEAR_SEGS = 32;
const LOD_FAR_SEGS = 8;
const LOD_NEAR_RADIUS = 1;

// Modul-Konstanten: nichts pro Frame allokieren.
const _color = new THREE.Color();
const _focus = { x: 0, y: 0, z: 0 };
const _startPos = trackPoint(STATIONS[0].s);

export class TerrainSystem {
  async init(ctx) {
    this.group = new THREE.Group();
    this.group.name = 'terrain';
    ctx.scene.add(this.group);

    // Vertexfarben tragen die Palette; Materialfarbe multipliziert neutral.
    this.material = mat('sandLight', { vertexColors: true, flatShading: false, _terrain: 1 });
    this.material.color.setRGB(1, 1, 1);

    /** @type {Map<string, TileHandle>} */
    this.active = new Map();
    /** Freie Handles pro LOD-Segmentzahl — Geometrie-Buffer wiederverwenden. */
    this.pool = new Map(); // segs → TileHandle[]
    /** @type {string[]} */
    this.queue = [];
    this.queueSet = new Set();

    this.centerTX = null;
    this.centerTZ = null;

    // Zentrum vorab bauen, damit der erste Frame nicht leer ist.
    const focus = this._focusOf(ctx);
    const tx = Math.floor(focus.x / TILE);
    const tz = Math.floor(focus.z / TILE);
    this.centerTX = tx;
    this.centerTZ = tz;
    this._syncDesired(tx, tz);
    // Burst: unmittelbare Nachbarschaft im init, Rest über Frames.
    this._drainQueue(12);
  }

  /**
   * Bequemlichkeit für Player/Scatter — delegiert an shared/terrain.js.
   * @param {number} x
   * @param {number} z
   * @param {number} [hint=-1]
   */
  heightAt(x, z, hint = -1) {
    return terrainHeight(x, z, hint);
  }

  update(_dt, ctx) {
    const focus = this._focusOf(ctx);
    const tx = Math.floor(focus.x / TILE);
    const tz = Math.floor(focus.z / TILE);

    if (tx !== this.centerTX || tz !== this.centerTZ) {
      this.centerTX = tx;
      this.centerTZ = tz;
      this._syncDesired(tx, tz);
    }

    this._drainQueue(BUILDS_PER_FRAME);
  }

  // --- Kachelverwaltung -----------------------------------------------------

  _syncDesired(cx, cz) {
    const desired = new Set();
    for (let dz = -HALF; dz <= HALF; dz++) {
      for (let dx = -HALF; dx <= HALF; dx++) {
        const tx = cx + dx;
        const tz = cz + dz;
        const key = tileKey(tx, tz);
        desired.add(key);

        const segs = lodSegs(dx, dz);
        const existing = this.active.get(key);
        if (existing && existing.segs === segs) continue;

        // LOD-Wechsel oder neu: alte Kachel zurück in den Pool, neu einreihen.
        if (existing) this._release(key, existing);
        if (!this.queueSet.has(key)) {
          this.queue.push(key);
          this.queueSet.add(key);
        }
      }
    }

    // Hinausgefallene recyceln.
    for (const [key, handle] of this.active) {
      if (!desired.has(key)) this._release(key, handle);
    }

    // Warteschlange von veralteten Keys bereinigen (Spieler ist weitergezogen).
    if (this.queue.length > 0) {
      const kept = [];
      for (const key of this.queue) {
        if (desired.has(key) && !this.active.has(key)) kept.push(key);
        else this.queueSet.delete(key);
      }
      this.queue = kept;
    }
  }

  _drainQueue(maxBuilds) {
    let built = 0;
    while (built < maxBuilds && this.queue.length > 0) {
      const key = this.queue.shift();
      this.queueSet.delete(key);
      if (this.active.has(key)) continue;

      const [tx, tz] = parseKey(key);
      // LOD relativ zum aktuellen Zentrum (nicht zum Einreihen-Zeitpunkt).
      const dx = tx - this.centerTX;
      const dz = tz - this.centerTZ;
      // Außerhalb des Fensters? Verwerfen.
      if (Math.abs(dx) > HALF || Math.abs(dz) > HALF) continue;

      const segs = lodSegs(dx, dz);
      const handle = this._acquire(segs);
      fillTileGeometry(handle.geometry, tx, tz, segs);
      handle.mesh.position.set(0, 0, 0);
      handle.mesh.visible = true;
      handle.tx = tx;
      handle.tz = tz;
      handle.segs = segs;
      handle.key = key;
      this.active.set(key, handle);
      built++;
    }
  }

  _acquire(segs) {
    const list = this.pool.get(segs);
    if (list && list.length > 0) {
      const h = list.pop();
      this.group.add(h.mesh);
      return h;
    }
    // Frische Geometrie — einmalig, danach nur noch Recycling.
    const geometry = makeTileGeometry(segs);
    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false; // Welt-Vertices, keine Kachel-Translation
    mesh.updateMatrix();
    this.group.add(mesh);
    return { geometry, mesh, segs, tx: 0, tz: 0, key: '' };
  }

  _release(key, handle) {
    this.active.delete(key);
    handle.mesh.visible = false;
    this.group.remove(handle.mesh);
    let list = this.pool.get(handle.segs);
    if (!list) {
      list = [];
      this.pool.set(handle.segs, list);
    }
    list.push(handle);
    // Auch aus der Queue werfen, falls doppelt.
    this.queueSet.delete(key);
  }

  _focusOf(ctx) {
    if (ctx.playerPos) {
      _focus.x = ctx.playerPos.x;
      _focus.y = ctx.playerPos.y;
      _focus.z = ctx.playerPos.z;
      return _focus;
    }
    // Vor dem ersten PlayerSystem-Update: Südbahnhof (CONTRACT).
    _focus.x = _startPos.x;
    _focus.y = _startPos.y;
    _focus.z = _startPos.z;
    return _focus;
  }

  dispose() {
    for (const h of this.active.values()) {
      h.geometry.dispose();
    }
    for (const list of this.pool.values()) {
      for (const h of list) h.geometry.dispose();
    }
    this.material?.dispose?.();
    this.active.clear();
    this.pool.clear();
  }
}

// --- LOD / Keys -------------------------------------------------------------

function lodSegs(dx, dz) {
  const cheb = Math.max(Math.abs(dx), Math.abs(dz));
  return cheb <= LOD_NEAR_RADIUS ? LOD_NEAR_SEGS : LOD_FAR_SEGS;
}

function tileKey(tx, tz) {
  return tx + ',' + tz;
}

function parseKey(key) {
  const c = key.indexOf(',');
  return [Number(key.slice(0, c)), Number(key.slice(c + 1))];
}

// --- Geometrie --------------------------------------------------------------

/**
 * Leere BufferGeometry für eine Kachel inkl. Schürze.
 * Layout: Grid (segs+1)², danach Schürzenvertices (4 × (segs+1)).
 */
export function makeTileGeometry(segs) {
  const gridN = segs + 1;
  const gridVerts = gridN * gridN;
  // Pro Kante segs+1 Vertices (Ecken doppelt — einfacher Index-Code, ~null Kosten).
  const skirtVerts = 4 * gridN;
  const vertCount = gridVerts + skirtVerts;

  const positions = new Float32Array(vertCount * 3);
  const colors = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);

  // Grid-Quads + 4 Schürzenstreifen.
  const quadCount = segs * segs + 4 * segs;
  const indices = new Uint32Array(quadCount * 6);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geo.setIndex(new THREE.BufferAttribute(indices, 1));
  // segs merken, damit fill weiß, was es füllt.
  geo.userData.segs = segs;
  return geo;
}

/**
 * Füllt eine Kachelgeometrie. Exportiert für scripts/check-terrain.js —
 * reiner Datenschreiber, braucht kein WebGL.
 *
 * @param {THREE.BufferGeometry} geo
 * @param {number} tx Kachel-X-Index
 * @param {number} tz Kachel-Z-Index
 * @param {number} segs
 * @returns {{ positions: Float32Array, segs: number, tx: number, tz: number }}
 */
export function fillTileGeometry(geo, tx, tz, segs) {
  const gridN = segs + 1;
  const gridVerts = gridN * gridN;
  const positions = geo.attributes.position.array;
  const colors = geo.attributes.color.array;
  const normals = geo.attributes.normal.array;
  const indices = geo.index.array;

  const originX = tx * TILE;
  const originZ = tz * TILE;
  const step = TILE / segs;

  // projectToTrack-Hinweis: s des vorigen Vertex. Entlang einer Zeile wandert
  // man seitlich, s ändert sich langsam — der Hinweis bleibt treffsicher und
  // spart die 20×-teure Globalsuche (CONTRACT).
  let hint = -1;

  // Erster Vertex: einmal ohne Hinweis, danach ketten.
  {
    const proj0 = projectToTrack(originX, originZ, -1);
    hint = proj0.s;
  }

  for (let iz = 0; iz < gridN; iz++) {
    for (let ix = 0; ix < gridN; ix++) {
      const x = originX + ix * step;
      const z = originZ + iz * step;
      const proj = projectToTrack(x, z, hint);
      hint = proj.s;
      const y = terrainHeightAt(x, z, proj);

      const vi = iz * gridN + ix;
      positions[vi * 3] = x;
      positions[vi * 3 + 1] = y;
      positions[vi * 3 + 2] = z;

      writeVertexColor(colors, vi, x, z, y, proj);
    }
    // Zeilenwechsel: x springt zurück, s-Hinweis der Zeile davor bleibt ok
    // (gleiche z-Nachbarschaft). hint vom letzten Vertex der Zeile behalten.
  }

  // --- Indizes des Grids ----------------------------------------------------
  let ii = 0;
  for (let iz = 0; iz < segs; iz++) {
    for (let ix = 0; ix < segs; ix++) {
      const a = iz * gridN + ix;
      const b = a + 1;
      const c = a + gridN;
      const d = c + 1;
      // CCW von oben.
      indices[ii++] = a; indices[ii++] = c; indices[ii++] = b;
      indices[ii++] = b; indices[ii++] = c; indices[ii++] = d;
    }
  }

  // --- Schürze: Kanten nach unten, verdeckt LOD-Spalte ----------------------
  // Reihenfolge der Kanten: -Z, +Z, -X, +X.
  const skirtBase = gridVerts;
  const edgeDefs = [
    // [gridIndex(i), x, z] für i = 0..segs
    {
      // -Z (iz = 0): ix von 0 → segs
      gridOf: (i) => i,
      skirtOf: (i) => skirtBase + i,
    },
    {
      // +Z (iz = segs)
      gridOf: (i) => segs * gridN + i,
      skirtOf: (i) => skirtBase + gridN + i,
    },
    {
      // -X (ix = 0): iz von 0 → segs
      gridOf: (i) => i * gridN,
      skirtOf: (i) => skirtBase + 2 * gridN + i,
    },
    {
      // +X (ix = segs)
      gridOf: (i) => i * gridN + segs,
      skirtOf: (i) => skirtBase + 3 * gridN + i,
    },
  ];

  for (const edge of edgeDefs) {
    for (let i = 0; i <= segs; i++) {
      const gi = edge.gridOf(i);
      const si = edge.skirtOf(i);
      positions[si * 3] = positions[gi * 3];
      positions[si * 3 + 1] = positions[gi * 3 + 1] - SKIRT_DEPTH;
      positions[si * 3 + 2] = positions[gi * 3 + 2];
      // Farbe vom Kantenvertex übernehmen (silhouetten-dunkel reicht).
      colors[si * 3] = colors[gi * 3] * 0.7;
      colors[si * 3 + 1] = colors[gi * 3 + 1] * 0.7;
      colors[si * 3 + 2] = colors[gi * 3 + 2] * 0.7;
    }
  }

  // Schürzen-Quads. Windung so, dass die Außenseite nach außen zeigt.
  // -Z: Blick nach −Z → a,b oben, a',b' unten; Windung a,a',b
  const skirtQuads = [
    { // -Z
      top: (i) => i,
      bot: (i) => skirtBase + i,
      flip: false,
    },
    { // +Z
      top: (i) => segs * gridN + i,
      bot: (i) => skirtBase + gridN + i,
      flip: true,
    },
    { // -X
      top: (i) => i * gridN,
      bot: (i) => skirtBase + 2 * gridN + i,
      flip: true,
    },
    { // +X
      top: (i) => i * gridN + segs,
      bot: (i) => skirtBase + 3 * gridN + i,
      flip: false,
    },
  ];

  for (const e of skirtQuads) {
    for (let i = 0; i < segs; i++) {
      const t0 = e.top(i), t1 = e.top(i + 1);
      const b0 = e.bot(i), b1 = e.bot(i + 1);
      if (!e.flip) {
        indices[ii++] = t0; indices[ii++] = b0; indices[ii++] = t1;
        indices[ii++] = t1; indices[ii++] = b0; indices[ii++] = b1;
      } else {
        indices[ii++] = t0; indices[ii++] = t1; indices[ii++] = b0;
        indices[ii++] = t1; indices[ii++] = b1; indices[ii++] = b0;
      }
    }
  }

  // --- Normalen: finiter Unterschied auf dem Grid, Schürze von Kante --------
  // Kein terrainNormal() pro Vertex — das wären 4× projectToTrack extra.
  for (let iz = 0; iz < gridN; iz++) {
    for (let ix = 0; ix < gridN; ix++) {
      const vi = iz * gridN + ix;
      const x0 = positions[vi * 3];
      const y0 = positions[vi * 3 + 1];
      const z0 = positions[vi * 3 + 2];

      // Nachbarn im Grid (am Rand clampen).
      const iL = iz * gridN + Math.max(0, ix - 1);
      const iR = iz * gridN + Math.min(segs, ix + 1);
      const iD = Math.max(0, iz - 1) * gridN + ix;
      const iU = Math.min(segs, iz + 1) * gridN + ix;

      const dx = positions[iR * 3] - positions[iL * 3];
      const dyx = positions[iR * 3 + 1] - positions[iL * 3 + 1];
      const dzx = positions[iR * 3 + 2] - positions[iL * 3 + 2];
      // „Rechts"-Kante
      const dxz = positions[iU * 3] - positions[iD * 3];
      const dyz = positions[iU * 3 + 1] - positions[iD * 3 + 1];
      const dzz = positions[iU * 3 + 2] - positions[iD * 3 + 2];

      // n = e_x × e_z
      let nx = dyx * dzz - dzx * dyz;
      let ny = dzx * dxz - dx * dzz;
      let nz = dx * dyz - dyx * dxz;
      // Falls die Kante in −X lag: Vorzeichen kann kippen — nach oben zwingen.
      if (ny < 0) { nx = -nx; ny = -ny; nz = -nz; }
      const len = Math.hypot(nx, ny, nz) || 1;
      normals[vi * 3] = nx / len;
      normals[vi * 3 + 1] = ny / len;
      normals[vi * 3 + 2] = nz / len;

      // silence unused — y0/x0/z0 nur für Klarheit der Nachbarschaft
      void y0; void x0; void z0;
    }
  }
  // Schürze: Normale horizontal nach außen (silhouettenweich).
  for (let s = 0; s < 4 * gridN; s++) {
    const si = skirtBase + s;
    const gi = s < gridN ? s
      : s < 2 * gridN ? segs * gridN + (s - gridN)
        : s < 3 * gridN ? (s - 2 * gridN) * gridN
          : (s - 3 * gridN) * gridN + segs;
    // Grobe Außenrichtung aus Kachelzentrum.
    const cx = originX + TILE * 0.5;
    const cz = originZ + TILE * 0.5;
    let nx = positions[si * 3] - cx;
    let nz = positions[si * 3 + 2] - cz;
    const len = Math.hypot(nx, nz) || 1;
    normals[si * 3] = nx / len;
    normals[si * 3 + 1] = 0.15;
    normals[si * 3 + 2] = nz / len;
    void gi;
  }

  geo.attributes.position.needsUpdate = true;
  geo.attributes.color.needsUpdate = true;
  geo.attributes.normal.needsUpdate = true;
  geo.index.needsUpdate = true;
  geo.computeBoundingSphere();

  return { positions, segs, tx, tz };
}

/**
 * Vertexfarbe aus Biom, Nähe zum Gleis, Höhe. Alles über varyColor/PALETTE.
 */
function writeVertexColor(colors, vi, x, z, y, proj) {
  const absLat = Math.abs(proj.lateral);
  const biome = biomeAt(proj.s);
  const seed = rand01(
    Math.floor(x * 0.5),
    Math.floor(z * 0.5),
    WORLD_SEED ^ 0xc01a
  );

  let key;
  // Schotterband nahe am Gleis — der Korridor soll nach Bahn aussehen.
  if (absLat < 4.2) {
    key = absLat < 2.8 ? 'ballast' : 'ballastDark';
  } else {
    switch (biome.id) {
      case 'playa':
        key = y < proj.trackY + 1 ? 'sand' : 'sandLight';
        break;
      case 'scrub':
        key = absLat < 18 ? 'sandDark' : (seed < 0.55 ? 'scrub' : 'sand');
        break;
      case 'rock':
        key = seed < 0.35 ? 'rockDark' : 'rock';
        // Steile Lagen wirken felsiger — laterale Distanz als Proxy für Relief.
        if (absLat > 30 && seed > 0.5) key = 'rockDark';
        break;
      case 'pine':
        key = seed < 0.65 ? 'pine' : (seed < 0.85 ? 'pineDark' : 'grass');
        break;
      case 'meadow':
        key = seed < 0.7 ? 'grass' : 'scrub';
        break;
      default:
        key = 'sand';
    }
  }

  // Senken abdunkeln (rel. zur Gleishöhe), Grate leicht aufhellen.
  const rel = y - proj.trackY;
  let amount = 0.11;
  let hex = varyColor(key, seed, amount);
  _color.setHex(hex);
  if (rel < -3) {
    const t = clamp01((-rel - 3) / 12);
    _color.multiplyScalar(1 - t * 0.35);
  } else if (rel > 8) {
    const t = clamp01((rel - 8) / 20);
    _color.offsetHSL(0, 0, t * 0.06);
  }

  colors[vi * 3] = _color.r;
  colors[vi * 3 + 1] = _color.g;
  colors[vi * 3 + 2] = _color.b;
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Nur die Grid-Höhen einer Kachel (ohne Schürze) — für den Kantentest.
 * Exportiert für scripts/check-terrain.js.
 */
export function sampleTileGrid(tx, tz, segs) {
  const gridN = segs + 1;
  // Float64: Kantentests und Höhenvergleich brauchen volle JS-Präzision.
  // Die GPU-Buffer bleiben Float32 — das ist ein reines Test-/CPU-API.
  const heights = new Float64Array(gridN * gridN);
  const xs = new Float64Array(gridN * gridN);
  const zs = new Float64Array(gridN * gridN);
  const originX = tx * TILE;
  const originZ = tz * TILE;
  const step = TILE / segs;
  let hint = -1;
  {
    const p0 = projectToTrack(originX, originZ, -1);
    hint = p0.s;
  }
  for (let iz = 0; iz < gridN; iz++) {
    for (let ix = 0; ix < gridN; ix++) {
      const x = originX + ix * step;
      const z = originZ + iz * step;
      const proj = projectToTrack(x, z, hint);
      hint = proj.s;
      const y = terrainHeightAt(x, z, proj);
      const vi = iz * gridN + ix;
      heights[vi] = y;
      xs[vi] = x;
      zs[vi] = z;
    }
  }
  return { heights, xs, zs, segs, gridN, originX, originZ, step };
}

export { TILE as TERRAIN_TILE_SIZE_LOCAL, LOD_NEAR_SEGS, LOD_FAR_SEGS };
